import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/LegalPages.css';

// ── Block type definitions ──────────────────────────────────────────────────

interface ClauseBlock {
  type: 'clause';
  num: string;
  html: string;
}

interface PriceTableRow {
  planHtml: string;
  autoTag: string;
  details: string;
}

interface PriceTableBlock {
  type: 'price-table';
  headers: [string, string];
  rows: PriceTableRow[];
}

interface WarningBlock {
  type: 'warning';
  html: string;
}

interface SubListBlock {
  type: 'sub-list';
  items: string[];
}

interface TimelineItem {
  title: string;
  body: string;
}

interface TimelineBlock {
  type: 'timeline';
  items: TimelineItem[];
}

interface StepsBlock {
  type: 'steps';
  items: string[];
}

interface ContactItem {
  label: string;
  href?: string;
  value: string;
}

interface ContactCardBlock {
  type: 'contact-card';
  items: ContactItem[];
}

type AutoRenewBlock =
  | ClauseBlock
  | PriceTableBlock
  | WarningBlock
  | SubListBlock
  | TimelineBlock
  | StepsBlock
  | ContactCardBlock;

interface AutoRenewSection {
  num: string;
  title: string;
  blocks: AutoRenewBlock[];
}

interface AppleNoticeData {
  title: string;
  items: string[];
}

interface NoticeData {
  title: string;
  body: string;
}

interface AutoRenewData {
  heroTitle: string;
  heroMeta: string;
  appleNotice: AppleNoticeData;
  notice: NoticeData;
  sections: AutoRenewSection[];
  footerNote: string;
}

// ── Block renderer ──────────────────────────────────────────────────────────

const renderBlock = (block: AutoRenewBlock, index: number): React.ReactNode => {
  switch (block.type) {
    case 'clause':
      return (
        <div key={index} className="ma-clause">
          {block.num && <span className="ma-clause-num">{block.num}</span>}{' '}
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: block.html }} />
        </div>
      );

    case 'price-table':
      return (
        <table key={index} className="ma-price-table">
          <thead>
            <tr>
              <th>{block.headers[0]}</th>
              <th>{block.headers[1]}</th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                <td>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: row.planHtml }} />
                  <span className="ma-auto-tag">{row.autoTag}</span>
                </td>
                <td>{row.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'warning':
      return (
        <div key={index} className="ma-warning">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: block.html }} />
        </div>
      );

    case 'sub-list':
      return (
        <ul key={index} className="ma-sub-list">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );

    case 'timeline':
      return (
        <div key={index} className="ma-timeline">
          {block.items.map((item, i) => (
            <div key={i} className="ma-timeline-item">
              <strong>{item.title}</strong>
              {item.body}
            </div>
          ))}
        </div>
      );

    case 'steps':
      return (
        <div key={index} className="ma-steps">
          {block.items.map((item, i) => (
            <div key={i} className="ma-step">
              <div className="ma-step-num">{i + 1}</div>
              {/* eslint-disable-next-line react/no-danger */}
              <div className="ma-step-text" dangerouslySetInnerHTML={{ __html: item }} />
            </div>
          ))}
        </div>
      );

    case 'contact-card':
      return (
        <div key={index} className="ma-contact-card">
          {block.items.map((item, i) => (
            <div key={i} className="ma-contact-item">
              <span className="ma-contact-label">{item.label}</span>
              {item.href ? (
                <a href={item.href} className="ma-contact-link">{item.value}</a>
              ) : (
                <span>{item.value}</span>
              )}
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

// ── Page component ──────────────────────────────────────────────────────────

const AutoRenewPage: React.FC = () => {
  const { t } = useTranslation();

  const data = t('autoRenewAgreement', { returnObjects: true }) as AutoRenewData;

  if (!data || !Array.isArray(data.sections)) {
    return (
      <div className="ma-page">
        <div className="ma-hero-banner">
          <div className="ma-hero-banner-inner">
            <div className="ma-doc-title">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  const { heroTitle, heroMeta, appleNotice, notice, sections, footerNote } = data;

  return (
    <div className="ma-page">
      {/* Hero Banner */}
      <div className="ma-hero-banner">
        <div className="ma-hero-banner-inner">
          <div className="ma-doc-title">{heroTitle}</div>
          <div className="ma-doc-meta">{heroMeta}</div>
        </div>
      </div>

      <div className="ma-content">
        {/* Apple compliance notice */}
        <div className="ma-apple-notice">
          <div className="ma-apple-notice-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1B5E20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {appleNotice.title}
          </div>
          <div className="ma-apple-notice-body">
            {appleNotice.items.map((item, i) => (
              /* eslint-disable-next-line react/no-danger */
              <p key={i} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </div>
        </div>

        {/* Notice */}
        <div className="ma-notice">
          <strong>{notice.title}</strong>
          {notice.body}
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <div key={section.num} className="ma-section">
            <div className="ma-section-title">
              <span className="ma-section-num">{section.num}</span>
              {section.title}
            </div>
            {section.blocks.map((block, i) => renderBlock(block, i))}
          </div>
        ))}

        <p className="ma-end-note">{footerNote}</p>
      </div>
    </div>
  );
};

export default AutoRenewPage;
